import type { PropTypes } from '@mui/material';
import {
  Card,
  type CardProps,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { FC, ReactNode, JSX } from 'react';
import React, { memo, useMemo } from 'react';
import {
  Iconify,
  Label,
  OneLineTypography,
  SvgIconStyle,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import ServiceProviderSvgIcon from '../assets/service_provider_icon.svg';
import { PaymentStatus } from '@pflegenavi/shared/api';

export interface ReceiptBatchMangopayPaymentCardProps {
  paidAmountInCents: number;
  paidAdditionalFeeAmountInCents: number;
  unpaidAmountInCents: number;
  unpaidAdditionalFeeAmountInCents: number;
  unpaidCount: number;
  cancelledAmountInCents?: number;
  cancelledCount?: number;
  deletedAmountInCents?: number;
  deletedCount?: number;
  serviceProviderName?: string;
  nursingHomeName?: string;
  initiatorName?: string;
  isLoading?: boolean;
  sx?: CardProps['sx'];
}

export const ReceiptBatchMangopayPaymentCard: FC<ReceiptBatchMangopayPaymentCardProps> =
  memo(({ sx, ...props }) => {
    return (
      <Card sx={{ p: 2, ...sx }}>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Header />
          <Divider
            sx={{
              // Counter the card padding
              ml: -2,
              mr: -2,
            }}
          />
          <Content {...props} />
        </Stack>
      </Card>
    );
  });

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface HeaderProps {}

const Header: FC<HeaderProps> = () => {
  const { t } = useTranslation();
  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        <Stack
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            height: 40,
            width: 40,
            backgroundColor: 'grey.300',
            borderRadius: 2,
          }}
        >
          <Iconify width={24} height={24} icon={'mdi:receipt-text'} />
        </Stack>
        <Stack
          sx={{
            gap: 0.5,
          }}
        >
          <OneLineTypography variant="subtitle2" fontWeight={700}>
            {t('receipts.batch.payment-card.title-mangopay')}
          </OneLineTypography>
          <OneLineTypography fontSize={14} color="grey.600">
            {t('receipts.batch.payment-card.subtitle')}
          </OneLineTypography>
        </Stack>
      </Stack>
    </Stack>
  );
};

type ContentProps = Pick<
  ReceiptBatchMangopayPaymentCardProps,
  | 'paidAmountInCents'
  | 'paidAdditionalFeeAmountInCents'
  | 'unpaidAmountInCents'
  | 'unpaidAdditionalFeeAmountInCents'
  | 'unpaidCount'
  | 'cancelledAmountInCents'
  | 'cancelledCount'
  | 'deletedAmountInCents'
  | 'deletedCount'
  | 'nursingHomeName'
  | 'serviceProviderName'
  | 'initiatorName'
  | 'isLoading'
>;

const Content: FC<ContentProps> = ({
  paidAmountInCents,
  paidAdditionalFeeAmountInCents,
  unpaidAmountInCents,
  unpaidAdditionalFeeAmountInCents,
  unpaidCount,
  cancelledAmountInCents,
  cancelledCount,
  deletedAmountInCents,
  deletedCount,
  nursingHomeName,
  serviceProviderName,
  initiatorName,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const paymentStatus = useMemo(() => {
    return unpaidCount > 0 ? 'PartiallyPaid' : PaymentStatus.Completed;
  }, [unpaidCount]);

  return (
    <Stack
      sx={{
        gap: 1.5,
      }}
    >
      <Row
        isLoading={isLoading}
        icon={<StatusIcon />}
        label={t('receipts.batch.payment-card.status-title')}
        value={<StatusLabel state={paymentStatus} />}
      />
      <Row
        isLoading={isLoading}
        icon={<CashIcon />}
        label={t('receipts.batch.payment-card.paid-amount')}
        value={
          <Typography
            sx={{
              fontSize: 14,
              color: 'success.main',
              textAlign: 'right',
              fontWeight: 700,
            }}
          >
            {fCurrencyCents(paidAmountInCents)}
          </Typography>
        }
      />
      {(paidAdditionalFeeAmountInCents > 0 ||
        unpaidAdditionalFeeAmountInCents > 0) && (
        <Row
          isLoading={isLoading}
          icon={<DotIcon />}
          label={t('receipts.batch.payment-card.paid-additional-fee-amount')}
          value={
            <Typography
              sx={{
                fontSize: 14,
                color: 'success.main',
                textAlign: 'right',
                fontWeight: 700,
              }}
            >
              {fCurrencyCents(paidAdditionalFeeAmountInCents)}
            </Typography>
          }
        />
      )}
      {unpaidCount > 0 && (
        <Row
          isLoading={isLoading}
          icon={<ReceiptIcon />}
          label={`${t(
            'receipts.batch.payment-card.unpaid-amount'
          )} (${unpaidCount})`}
          value={
            <Typography
              sx={{
                fontSize: 14,
                color: 'warning.dark',
                textAlign: 'right',
                fontWeight: 700,
              }}
            >
              {fCurrencyCents(unpaidAmountInCents)}
            </Typography>
          }
        />
      )}
      {unpaidCount > 0 &&
        (unpaidAdditionalFeeAmountInCents > 0 ||
          paidAdditionalFeeAmountInCents > 0) && (
          <Row
            isLoading={isLoading}
            icon={<DotIcon />}
            label={`${t(
              'receipts.batch.payment-card.unpaid-additional-fee-amount'
            )} (${unpaidCount})`}
            value={
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'warning.dark',
                  textAlign: 'right',
                  fontWeight: 700,
                }}
              >
                {fCurrencyCents(unpaidAdditionalFeeAmountInCents)}
              </Typography>
            }
          />
        )}
      {cancelledCount != null &&
        cancelledCount > 0 &&
        cancelledAmountInCents != null && (
          <Row
            isLoading={isLoading}
            icon={<ReceiptIcon />}
            label={`${t(
              'receipts.batch.payment-card.cancelled-amount'
            )} (${cancelledCount})`}
            value={
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'error.main',
                  textAlign: 'right',
                  fontWeight: 700,
                }}
              >
                {fCurrencyCents(cancelledAmountInCents)}
              </Typography>
            }
          />
        )}
      {deletedCount != null &&
        deletedCount > 0 &&
        deletedAmountInCents != null && (
          <Row
            isLoading={isLoading}
            icon={<ReceiptIcon />}
            label={`${t(
              'receipts.batch.payment-card.deleted-amount'
            )} (${deletedCount})`}
            value={
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'error.main',
                  textAlign: 'right',
                  fontWeight: 700,
                }}
              >
                {fCurrencyCents(deletedAmountInCents)}
              </Typography>
            }
          />
        )}
      {serviceProviderName && (
        <Row
          isLoading={isLoading}
          icon={<ServiceProviderIcon />}
          label={t('receipts.batch.payment-card.service-provider')}
          value={
            <Typography
              sx={{
                fontSize: 14,
                color: 'text.primary',
                textAlign: 'right',
              }}
            >
              {serviceProviderName}
            </Typography>
          }
        />
      )}
      {nursingHomeName && (
        <Row
          isLoading={isLoading}
          icon={<NursingHomeIcon />}
          label={t('receipts.batch.payment-card.nursing-home')}
          value={
            <Typography
              sx={{
                fontSize: 14,
                color: 'text.primary',
                textAlign: 'right',
              }}
            >
              {nursingHomeName}
            </Typography>
          }
        />
      )}
      {initiatorName && (
        <Row
          isLoading={isLoading}
          icon={<EmployeeIcon />}
          label={t('receipts.batch.payment-card.initiator')}
          value={
            <Typography
              sx={{
                fontSize: 14,
                color: 'text.primary',
                textAlign: 'right',
              }}
            >
              {initiatorName}
            </Typography>
          }
        />
      )}
    </Stack>
  );
};

interface RowProps {
  icon: ReactNode;
  label: ReactNode;
  value: ReactNode;
  isLoading?: boolean;
}

const Row: FC<RowProps> = ({ icon, label, value, isLoading }) => {
  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Stack
          sx={{
            width: 18,
          }}
        >
          {icon}
        </Stack>
        <OneLineTypography fontSize={14} color="text.primary">
          {label}
        </OneLineTypography>
      </Stack>
      {isLoading && (
        <Typography
          sx={{
            fontSize: 14,
            color: 'text.primary',
          }}
        >
          <Skeleton variant="text" width={100} />
        </Typography>
      )}
      {!isLoading && value}
    </Stack>
  );
};

const StatusIcon = ({ color }: { color?: string }): JSX.Element => {
  return (
    <Iconify sx={{ mt: '-2px' }} icon="mdi:cog" color={color ?? 'grey.600'} />
  );
};

const DotIcon = ({ color }: { color?: string }): JSX.Element => {
  return (
    <Iconify sx={{ mt: '-2px' }} icon="mdi:dot" color={color ?? 'grey.600'} />
  );
};

const CashIcon = ({ color }: { color?: string }) => {
  return (
    <Iconify
      sx={{ mt: '-2px' }}
      icon="majesticons:euro-circle"
      color={color ?? 'grey.600'}
    />
  );
};

const ReceiptIcon = ({ color }: { color?: string }) => {
  return (
    <Iconify
      sx={{ mt: '-1px' }}
      icon={'mdi:receipt-text'}
      color={color ?? 'grey.600'}
    />
  );
};

const NursingHomeIcon = ({ color }: { color?: string }) => {
  return (
    <Iconify sx={{ mt: '-2px' }} icon="mdi:home" color={color ?? 'grey.600'} />
  );
};

const ServiceProviderIcon = ({ color }: { color?: PropTypes.Color }) => {
  const theme = useTheme();
  return (
    <SvgIconStyle
      src={ServiceProviderSvgIcon}
      sx={{
        width: 16,
        height: 16,
        ml: '2px',
        mr: '-1px',
        mt: '-4px',
        color: color ?? theme.palette.grey['600'],
      }}
    />
  );
};

const EmployeeIcon = () => {
  return (
    <Iconify sx={{ mt: '-4px' }} icon="eva:person-fill" color="grey.600" />
  );
};

interface StatusLabel {
  state: PaymentStatus | 'PartiallyPaid';
}

const StatusLabel: FC<StatusLabel> = ({ state }) => {
  const { t } = useTranslation();

  const color = useMemo(() => {
    switch (state) {
      case PaymentStatus.Pending:
        return 'info';
      case PaymentStatus.Completed:
        return 'success';
      case 'PartiallyPaid':
        return 'warning';
      case PaymentStatus.Failed:
        return 'error';
      case PaymentStatus.Disputed:
        return 'error';
      default:
        return 'default';
    }
  }, [state]);

  return (
    <Label color={color}>
      {t(`receipts.batch.payment-card.status.${state}`)}
    </Label>
  );
};
